import { faColumns3 } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { CloseOutlined, Search } from '@mui/icons-material';
import {
  Box,
  IconButton,
  Input,
  MenuItem,
  Popover,
  Tooltip,
} from '@mui/material';
import { matchSorter } from 'match-sorter';
import React, { useState } from 'react';
import { CheckBox } from '../../../inputs/Checkbox';
import { useTableState } from '../providers/TableStateProvider';

const ColumnSelector = () => {
  const [anchorEl, setAnchorEl] = useState(null);

  const handleClick = (event: any) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);
  const id = open ? 'column-selector-popover' : undefined;

  return (
    <>
      <Tooltip title="Column Selector" enterDelay={600} enterNextDelay={600}>
        <IconButton size="small" onClick={handleClick}>
          <FontAwesomeIcon icon={faColumns3} />
        </IconButton>
      </Tooltip>

      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
      >
        <ColumnsDisplay />
      </Popover>
    </>
  );
};

export default ColumnSelector;

const ColumnsDisplay = () => {
  const tableState = useTableState();
  const [searchTerm, setSearchTerm] = React.useState('');
  const allCols = tableState.table.getAllColumns();
  const filteredOptions = React.useMemo(() => {
    return matchSorter(allCols, searchTerm, {
      keys: ['columnDef.header'],
    });
  }, [allCols, searchTerm]);

  return (
    <Box p={1} width="17rem" maxHeight="30rem">
      <SearchModule value={searchTerm} onChange={setSearchTerm} />
      <Box>
        {filteredOptions.map((col) => {
          if (!col.getCanHide()) {
            return null;
          }

          return (
            <MenuItem
              key={col.id}
              value={col.id}
              onClick={() => col.toggleVisibility()}
              sx={{
                whiteSpace: 'normal',
              }}
            >
              <CheckBox checked={col.getIsVisible()} />
              {col.columnDef.header as React.ReactNode}
            </MenuItem>
          );
        })}
      </Box>
    </Box>
  );
};

type SearchModuleProps = {
  value: string;
  onChange: (input: string) => void;
};

const SearchModule = ({ value, onChange }: SearchModuleProps) => {
  return (
    <Box position="relative" pt="0.15rem">
      <Input
        placeholder="Search..."
        value={value}
        onChange={(e) => onChange(e.target.value)}
        autoFocus
        sx={{
          '& .MuiInput-input': {
            border: 'none',
          },
          ' & .MuiInput-input:focus': {
            background: (theme) => theme.palette.background.active,
          },
          opacity: (theme) => (theme.palette.mode === 'dark' ? 1 : 0.8),
        }}
      />

      <Box
        position="absolute"
        top="50%"
        right={3}
        style={{ transform: 'translateY(-50%)' }}
      >
        <IconButton onClick={() => onChange('')}>
          {value === '' ? <Search /> : <CloseOutlined />}
        </IconButton>
      </Box>
    </Box>
  );
};
